/**
 * useCopilotTabControl — builds the copilot TabControl panel from each graphic
 * definition's `v_config.copilot.shortcuts` and owns the Maestro modal state.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { COPILOT_EXTRA_ACTIONS } from "../../config/copilotTabGroups";

const FALLBACK_TAB = "Other";

export const normalizeTabName = (name) =>
  String(name ?? "").toLowerCase().replace(/[\s_\-\/]+/g, "");

export const findTabByTarget = (tabs, target) => {
  if (!Array.isArray(tabs) || tabs.length === 0) return -1;
  if (typeof target === "number") {
    return target >= 0 && target < tabs.length ? target : -1;
  }
  const norm = normalizeTabName(target);
  let idx = tabs.findIndex((t) => normalizeTabName(t.name) === norm);
  if (idx !== -1) return idx;

  if (norm === "inmatch" || norm === "live") {
    idx = tabs.findIndex((t) => {
      const n = normalizeTabName(t.name);
      return n === "inmatch" || n === "live";
    });
  } else if (
    norm === "htft" ||
    norm === "halftime" ||
    norm === "ht" ||
    norm === "fulltime" ||
    norm === "ft"
  ) {
    idx = tabs.findIndex((t) => {
      const n = normalizeTabName(t.name);
      return n === "htft" || n === "halftime" || n === "ht" || n === "fulltime" || n === "ft";
    });
  } else if (norm === "prematch" || norm === "pregame") {
    idx = tabs.findIndex((t) => {
      const n = normalizeTabName(t.name);
      return n === "prematch" || n === "pregame";
    });
  }
  return idx;
};

/** The stats are for the live match, so only IN-MATCH shows them. */
export const showsStats = (tabName) => normalizeTabName(tabName) === "inmatch";

// A seat in the tab's 5-column grid: row "1".. and column "A".."E", as numbers.
const COLUMNS = "ABCDE";
const toPlace = (row, column) => {
  const r = Number(row);
  const c = COLUMNS.indexOf(String(column ?? "").toUpperCase()) + 1;
  return r > 0 && c > 0 ? { row: r, column: c } : null;
};

// `group` lists tabs as [{ name, row, column }] or a plain string (older shape).
const groupPlaces = (group) =>
  [].concat(group ?? [])
    .map((g) => (typeof g === "string" ? { name: g } : g))
    .filter((g) => g?.name)
    .map((g) => ({ name: g.name, place: toPlace(g.row, g.column) }));

// A shortcut's own group wins; otherwise the graphic-level `copilot.group`.
// A tab listed twice counts once, at its first seat.
const tabPlacesFor = (shortcutGroup, graphicGroup) => {
  const own = groupPlaces(shortcutGroup);
  const places = own.length ? own : groupPlaces(graphicGroup);
  if (!places.length) return [{ name: FALLBACK_TAB, place: null }];
  return places.filter((p, i) => places.findIndex((q) => q.name === p.name) === i);
};

// Match tabs in running order; any other tab follows in the order it first appears.
const TAB_ORDER = ["pre-match", "in-match", "ht/ft"];
const tabRank = (name) => {
  const i = TAB_ORDER.indexOf(name.toLowerCase());
  return i === -1 ? TAB_ORDER.length : i;
};

export function buildPanelConfig(definitions, driveCaptions) {
  // Keyed case-insensitively ("other" and "Other" are one tab, named as first seen);
  // Map keeps insertion order, so tabs follow the order they first appear in the definitions.
  const groups = new Map();
  const addToTab = (name, action, atStart = false) => {
    const key = name.toLowerCase();
    if (!groups.has(key)) groups.set(key, { name, actions: [] });
    if (atStart) groups.get(key).actions.unshift(action);
    else groups.get(key).actions.push(action);
  };
  const coveredGraphics = new Set();
  const presets = Array.isArray(driveCaptions) ? driveCaptions : [];

  for (const preset of presets) {
    const def = preset && definitions[preset.id];
    const shortcuts = def?.v_config?.copilot?.shortcuts;
    const entries = shortcuts
      ? [
          ...(Array.isArray(shortcuts.internal) ? shortcuts.internal : []),
          ...(Array.isArray(shortcuts.external) ? shortcuts.external : []),
        ].filter((s) => s && s.target)
      : [];
    if (entries.length === 0) continue;
    if (def?.id) coveredGraphics.add(def.id);

    for (const s of entries) {
      // The button's own words travel with the invocation, so the caption
      // window can title itself the way the operator's button is titled. A
      // definition's `name` is the raw graphic id on most graphics.
      const displayName = s.nameOverride || s.target;

      let handler;
      if (s.type === "component") {
        handler = { opusComponent: s.target, props: s.props || {} };
      } else if (s.type === "graphic") {
        coveredGraphics.add(s.target);
        handler = { opusComponent: "Maestro", props: { graphic: s.target, displayName } };
      } else {
        handler = {
          opusComponent: "Maestro",
          props: { graphic: def.id, initialCaption: s.target, displayName },
        };
      }

      const action = {
        triggerType: "BUTTON",
        displayName,
        shortcut: s.shortcut || {},
        type: "component",
        handler,
      };
      const tabs = tabPlacesFor(s.group, def.v_config.copilot.group);
      // One copy per tab: the same button can sit in a different cell on each tab.
      for (const { name, place } of tabs) addToTab(name, place ? { ...action, place } : action);
    }
  }

  // App-level buttons that aren't graphic captions (e.g. the "Build Subs" dialog).
  for (const extra of COPILOT_EXTRA_ACTIONS) {
    if (!extra || !extra.opusComponent) continue;
    const action = {
      triggerType: "BUTTON",
      displayName: extra.displayName || extra.opusComponent,
      shortcut: extra.shortcut || {},
      type: "component",
      handler: { opusComponent: extra.opusComponent, props: extra.props || {} },
    };
    for (const { name, place } of tabPlacesFor(extra.group)) {
      addToTab(name, place ? { ...action, place } : action, !place);
    }
  }

  // Default tab — renderer graphics not covered by a copilot group, each opening
  // the whole graphic in Maestro.
  const defaultActions = [];
  const seenGraphics = new Set();
  for (const preset of presets) {
    if (!preset) continue;
    const def = definitions[preset.id];
    const graphicId = def?.id || preset.captionDefinition?.id || preset.label;
    if (!graphicId || seenGraphics.has(graphicId)) continue;
    seenGraphics.add(graphicId);
    if (coveredGraphics.has(graphicId)) continue;
    defaultActions.push({
      triggerType: "BUTTON",
      displayName: def?.name || preset.label || graphicId,
      shortcut: {},
      type: "component",
      handler: {
        opusComponent: "Maestro",
        props: { graphic: graphicId, displayName: def?.name || preset.label || graphicId },
      },
    });
  }

  const tabs = [...groups.values()].sort((a, b) => tabRank(a.name) - tabRank(b.name));
  // Default tab disabled for now — re-enable to surface uncovered graphics.
  // if (defaultActions.length) {
  //   tabs.push({ name: "Default", actions: defaultActions });
  // }

  return { tabs };
}

export function useCopilotTabControl({ driveCaptions, clockHandler, tabContainerRef, onMiss }) {
  const [maestroOpen, setMaestroOpen] = useState(false);
  const [activeGraphic, setActiveGraphic] = useState(null);
  const [activeCaption, setActiveCaption] = useState(null);
  const [definitions, setDefinitions] = useState({});
  // True once every preset's definition has loaded, so the panel can render all
  // buttons at once instead of popping the graphic ones in after a fetch.
  const [ready, setReady] = useState(false);
  const definitionCacheRef = useRef({});
  const clockHandlerRef = useRef(null);
  const onMissRef = useRef(null);

  clockHandlerRef.current = clockHandler;
  onMissRef.current = onMiss;

  // Fetch each preset's full definition (carries v_config.copilot + schema +
  // v_ui + v_captions); cache for instant opens and publish to state so the
  // panel rebuilds when they land.
  useEffect(() => {
    if (!driveCaptions?.length) return;
    let cancelled = false;

    const tasks = driveCaptions.map(async (preset) => {
      if (!preset) return null;
      const cached = definitionCacheRef.current[preset.id];
      if (cached) return [preset.id, cached];

      const def = preset.captionDefinition;
      if (!def?.path || def.schema || def.v_config) {
        definitionCacheRef.current[preset.id] = def;
        return def ? [preset.id, def] : null;
      }
      try {
        const res = await fetch(def.path);
        if (!res.ok) return null;
        const raw = await res.json();
        const full = { ...raw, id: def.id, name: def.name, path: def.path };
        definitionCacheRef.current[preset.id] = full;
        return [preset.id, full];
      } catch {
        return null;
      }
    });

    Promise.all(tasks).then((results) => {
      if (cancelled) return;
      const next = {};
      for (const entry of results) {
        if (entry) next[entry[0]] = entry[1];
      }
      if (Object.keys(next).length) {
        setDefinitions((prev) => ({ ...prev, ...next }));
      }
      setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [driveCaptions]);

  const { tabs } = useMemo(
    () => buildPanelConfig(definitions, driveCaptions),
    [definitions, driveCaptions]
  );
  const panelConfig = useMemo(() => ({ tabs }), [tabs]);
  // What TabControl draws: shortcut keys aren't shown on the buttons (or their
  // tooltips); keyboard shortcuts still run from panelConfig.
  const buttonConfig = useMemo(
    () => ({ tabs: tabs.map((t) => ({ ...t, actions: t.actions.map((a) => ({ ...a, shortcut: {} })) })) }),
    [tabs],
  );

  // TabControl opens on its first tab and reports changes via onTabChange.
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const activeTabIndexRef = useRef(activeTabIndex);
  activeTabIndexRef.current = activeTabIndex;

  const activeTabName = tabs[Math.min(activeTabIndex, Math.max(0, tabs.length - 1))]?.name;
  const showStats = showsStats(activeTabName);

  const applyDomTabClick = useCallback(
    (idx) => {
      const container = tabContainerRef?.current || document.querySelector('[data-copilot-tabs="true"]');
      if (!container) return;
      const tabEls = container.querySelectorAll('[role="tab"]');
      const tabEl = tabEls[idx];
      if (tabEl) {
        tabEl.click();
        tabEl.focus();
      }
    },
    [tabContainerRef]
  );

  const switchTab = useCallback(
    (target) => {
      const idx = findTabByTarget(tabs, target);
      if (idx === -1) return;

      if (idx !== activeTabIndexRef.current) {
        setActiveTabIndex(idx);
        applyDomTabClick(idx);
      }
    },
    [tabs, applyDomTabClick]
  );

  const handleTabChange = useCallback((n) => {
    setActiveTabIndex(n);
  }, []);

  const openMaestro = useCallback(async ({ graphic, initialCaption, displayName, displaySubtitle }) => {
    const preset = driveCaptions.find((p) =>
      p.id === graphic || p.id === `preset_dyn_${graphic}` || p.id.endsWith(`_${graphic}`)
    );
    if (!preset) {
      onMissRef.current?.(graphic);
      return;
    }
    let fullDef = definitionCacheRef.current[preset.id];
    if (!fullDef) {
      fullDef = preset.captionDefinition;
      if (fullDef?.path && !fullDef.schema) {
        try {
          const res = await fetch(fullDef.path);
          if (res.ok) {
            const raw = await res.json();
            fullDef = { ...raw, id: fullDef.id, name: fullDef.name, path: fullDef.path };
          }
        } catch {}
      }
      definitionCacheRef.current[preset.id] = fullDef;
    }
    setActiveGraphic({ ...preset, captionDefinition: fullDef, displayName, displaySubtitle });
    setActiveCaption(initialCaption);
    setMaestroOpen(true);
  }, [driveCaptions]);

  const clockCommand = useCallback(
    (id) => () => clockHandlerRef.current?.({ id }),
    []
  );

  const invokers = useMemo(() => ({
    Maestro: openMaestro,
    clockReset:     clockCommand("reset"),
    clockStart1H:   clockCommand("startFirstHalf"),
    clockEnd1H:     clockCommand("endFirstHalf"),
    clockStart2H:   clockCommand("startSecondHalf"),
    clockEnd2H:     clockCommand("endSecondHalf"),
    clockOverwrite: clockCommand("overwrite"),
  }), [openMaestro, clockCommand]);

  const closeMaestro = useCallback(() => setMaestroOpen(false), []);

  return {
    maestroOpen,
    activeGraphic,
    activeCaption,
    closeMaestro,
    invokers,
    panelConfig,
    buttonConfig,
    ready,
    activeTabIndex,
    switchTab,
    onTabChange: handleTabChange,
    showStats,
  };
}
